import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Edit2,
  Grid3x3,
  MinusCircle,
  Plus,
  Trash2,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { generateTemplateBundles } from "@/features/bundleTemplateEngine";
import type { BundleTemplate } from "@/types/bundleTemplate";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/tools/bundle-templates")({
  component: () => (
    <ManagerOnly>
      <BundleTemplatesPage />
    </ManagerOnly>
  ),
});

function BundleTemplatesPage() {
  const navigate = useNavigate();
  const {
    bundleTemplates,
    deleteBundleTemplate,
    cards,
    cardsLoaded,
    cardsLoading,
    loadCards,
  } = useApp();

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // TNK-155 phase D made the cards set lazy-loaded; phase E gates this
  // template-availability page on it. Trigger the load explicitly here so
  // a deep-link straight to /tools/bundle-templates doesn't have to wait
  // for AppContext's post-hydration auto-trigger.
  useEffect(() => {
    if (!cardsLoaded && !cardsLoading) void loadCards();
  }, [cardsLoaded, cardsLoading, loadCards]);

  const sorted = useMemo(
    () =>
      [...bundleTemplates].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      ),
    [bundleTemplates],
  );

  // Per-template availability against current inventory.
  const liveMatches = useMemo(() => {
    const unlisted = cards.filter(
      (c) => c.listingStatus === "Unlisted" && c.quantity > 0,
    );
    const out: Record<string, { cardCount: number; availabilityPct: number }> =
      {};
    for (const template of bundleTemplates) {
      const result = generateTemplateBundles(unlisted, [template], new Set());
      const first = result[0];
      out[template.id] = {
        cardCount: first?.cardCount ?? 0,
        availabilityPct: first?.availabilityPct ?? 0,
      };
    }
    return out;
  }, [bundleTemplates, cards]);

  const handleDelete = (id: string) => {
    if (confirmDeleteId === id) {
      deleteBundleTemplate(id);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(id);
    }
  };

  const handleEdit = (id: string) => {
    navigate({ to: "/tools/bundle-template-edit", search: { id } });
  };

  const handleNew = () => {
    navigate({ to: "/tools/bundle-template-edit", search: {} });
  };

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <Link
          to="/tools"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Tools
        </Link>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto py-4 pr-2">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-xl font-bold text-foreground">
              Bundle templates
            </h1>
            <p className="text-sm leading-snug text-muted-foreground">
              Recipes the Studio uses to find matching cards in your inventory.
            </p>
          </div>
          <button
            type="button"
            onClick={handleNew}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
        </div>

        {sorted.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-6 text-center">
            <Grid3x3 className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm font-bold text-foreground">
              No bundle templates yet
            </div>
            <p className="text-sm leading-snug text-muted-foreground">
              Create a recipe (name + intent + filters + target count) and the
              Studio will surface matches against current inventory
              automatically.
            </p>
            <button
              type="button"
              onClick={handleNew}
              className="mt-2 rounded-md bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
            >
              Create your first template
            </button>
          </div>
        ) : (
          sorted.map((tmpl) => {
            const live = liveMatches[tmpl.id] ?? {
              cardCount: 0,
              availabilityPct: 0,
            };
            const isReady = cardsLoaded && live.availabilityPct >= 100;
            const isClose =
              cardsLoaded && !isReady && live.availabilityPct >= 80;
            const isConfirmDelete = confirmDeleteId === tmpl.id;
            const Icon = !cardsLoaded
              ? Circle
              : isReady
                ? CheckCircle2
                : isClose
                  ? Circle
                  : MinusCircle;
            const toneClass = !cardsLoaded
              ? "text-muted-foreground"
              : isReady
                ? "text-emerald-600"
                : isClose
                  ? "text-amber-600"
                  : "text-muted-foreground";
            return (
              <div
                key={tmpl.id}
                className={`space-y-2 rounded-md border bg-card p-3 ${
                  isConfirmDelete ? "border-destructive" : "border-border"
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-foreground">
                      {tmpl.name}
                    </div>
                    <div className="truncate text-[11px] font-medium capitalize text-muted-foreground">
                      {tmpl.intent} · target {tmpl.targetCount}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isConfirmDelete ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          className="rounded-md bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground"
                        >
                          Keep
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(tmpl.id)}
                          className="rounded-md bg-destructive px-2.5 py-1 text-xs font-semibold text-destructive-foreground"
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => handleEdit(tmpl.id)}
                          aria-label="Edit template"
                          className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(tmpl.id)}
                          aria-label="Delete template"
                          className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div
                  className={`flex items-center gap-1.5 text-xs font-semibold ${toneClass}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>
                    {cardsLoaded
                      ? `${live.availabilityPct}% available · ${live.cardCount} / ${tmpl.targetCount} cards`
                      : "Loading inventory…"}
                  </span>
                </div>

                <p className="line-clamp-3 text-xs leading-snug text-muted-foreground">
                  {filterSummary(tmpl)}
                </p>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function filterSummary(template: BundleTemplate): string {
  const parts: string[] = [];
  const f = template.filters;
  if (f.game) parts.push(f.game);
  if (f.supertypes?.length) parts.push(`Supertype: ${f.supertypes.join(", ")}`);
  if (f.types?.length) parts.push(`Types: ${f.types.join(", ")}`);
  if (f.rarities?.length) parts.push(`Rarities: ${f.rarities.join(", ")}`);
  if (f.sets?.length)
    parts.push(
      `Sets: ${f.sets.slice(0, 3).join(", ")}${f.sets.length > 3 ? "…" : ""}`,
    );
  if (f.series?.length)
    parts.push(`Series: ${f.series.slice(0, 2).join(", ")}`);
  if (f.conditions?.length)
    parts.push(`Conditions: ${f.conditions.join(", ")}`);
  if (f.maxPricePerCard != null)
    parts.push(`≤ $${f.maxPricePerCard.toFixed(2)}/card`);
  return parts.length === 0
    ? "No filters — matches any unlisted card."
    : parts.join(" · ");
}
